import { CountryData } from 'emoji-flags';
import { SortBy, SortOrder } from '../alerts/models/alerts.model';

enum DocumentsRequestType {
  KYC = 'KYC',
  AFFORDABILITY = 'AFFORDABILITY',
  EDD = 'EDD',
  CARD = 'CARD',
}

export enum DocumentsRequestStatus {
  OPEN = 'OPEN',
  SUBMITTED = 'SUBMITTED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  FRAUD = 'FRAUD',
  UNDEFINED = 'UNDEFINED',
}

export enum DocumentationStatus {
  PENDING = 'PENDING',
  CHANGE_REQUESTED = 'CHANGE_REQUESTED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

export enum DocumentationFileStatus {
  SUBMITTED = 'SUBMITTED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

export enum DocumentsRequestCategory {
  PHOTO_ID = 'PHOTO_ID',
  PROOF_OF_ADDRESS = 'PROOF_OF_ADDRESS',
  CARD_VERIFICATION = 'CARD_VERIFICATION',
  INCOME_AFFORDABILITY = 'INCOME_AFFORDABILITY',
  INCOME_EDD = 'INCOME_EDD',
  WIN_VERIFICATION = 'WIN_VERIFICATION',
  BANK_VERIFICATION = 'BANK_VERIFICATION',
}

export interface RequestDocumentsDTO {
  playerId: string;
  documentCategories: string[];
  cardsIds: string[];
  reason: string;
  internalCommentary: string;
  publicCommentary: string;
  emailContent: string;
  emailSubject: string;
  emailTemplateId: string;
}

export interface DocumentsRequestPageEntryDTO {
  id: number;
  playerId: number;
  dateCreated: Date;
  zendeskId?: string;
  zendeskTags?: string;
  gbgResult?: string;
  gbgDate?: Date;
  gbgId?: string;
  gbgScore?: number;
  type: DocumentsRequestType;
  playerCountryCode?: string;
  status?: DocumentsRequestStatus;
  requestor: string;
  dateUpdated: Date;
  emailTemplateId: string;
  categories: DocumentsRequestCategory[];
  lock?: DocumentLockDTO;
}

export interface DocumentsRequestPageEntry {
  id: number;
  playerId: number;
  dateCreated: Date;
  zendeskId?: string;
  zendeskTags?: string;
  gbgResult?: string;
  gbgDate?: Date;
  gbgId?: string;
  gbgScore?: number;
  expanded?: boolean;
  type?: DocumentsRequestType;
  playerCountry?: CountryData;
  status?: DocumentsRequestStatus;
  requestor: string;
  dateUpdated: Date;
  emailTemplateId?: string;
  categories: DocumentsRequestCategory[];
  lock?: DocumentLockDTO;
}

export interface urlParameters {
  pageIndex: number;
  pageSize: number;
  id: number;
  sortBy: SortBy;
  order: SortOrder;
  tabName: string;
  playerId?: number;
  lockedBy?: string;
}

export interface DocumentRequestDTO {
  id: number;
  playerId: number;
  zendeskId: number;
  dateCreated: Date;
  reason: string;
  internalCommentary: string;
  publicCommentary: string;
  status: DocumentsRequestStatus;
  categories: DocumentationCategoryDTO[];
  cardsVerificationRequests: CardVerificationRequestDTO[];
  requestor: string;
  dateUpdated: Date;
  lock?: DocumentLockDTO;
}

export interface DocumentRequestCreatedDTO {
  id: number;
  playerId: number;
}

export interface CardVerificationRequestDTO {
  id: number;
  name: string;
  code: string;
  description: string;
  icon: string;
  max: number;
  documentation: DocumentationDTO;
  cardId: string;
}

export interface DocumentationCategoryDTO {
  name: string;
  code: string;
  description: string;
  icon: string;
  max: number;
  documentation: DocumentationDTO[];
}

export interface DocumentationDTO {
  id: number;
  name: string;
  code: string;
  description: string;
  status: DocumentationStatus;
  icon: string;
  max: number;
  reviewComment: string;
  files: DocumentationFileDTO[];
  rejectionComment: string;
}

export interface DocumentationFileDTO {
  id: number;
  name: string;
  status: DocumentationFileStatus;
  size: number;
}

export interface UpdateDocumentationFileStatusDTO {
  status: DocumentationFileStatus;
}

export interface DocumentsRequestHistoryEntryDTO {
  id: number;
  requestId: number;
  author: string;
  title: string;
  internalNote?: string;
  dateCreated: Date;
  expanded?: boolean;
}

export interface DocumentRequest {
  id: number;
  playerId: number;
  zendeskId: number;
  dateCreated: Date;
  reason: string;
  internalCommentary: string;
  publicCommentary: string;
  status: DocumentsRequestStatus;
  categories: DocumentationCategory[];
  cardsVerificationRequests: CardVerificationRequest[];
  requestor: string;
  dateUpdated: Date;
  lock?: DocumentLock;
}

export interface CardVerificationRequest {
  id: number;
  name: string;
  code: string;
  description: string;
  icon: string;
  max: number;
  documentation: Documentation;
  cardId: string;
}

export interface DocumentationCategory {
  name: string;
  code: string;
  description: string;
  icon: string;
  max: number;
  documentation: Documentation[];
}

export interface Documentation {
  id?: number;
  name: string;
  code: string;
  description: string;
  status: DocumentationStatus;
  icon: string;
  max: number;
  rejectionComment: string;
  files: DocumentationFile[];
}

export interface DocumentationFile {
  id: number;
  name: string;
  status: DocumentationFileStatus;
  size: number;
}

export interface RejectDocumentationDTO {
  rejectionComment: string;
  rejectWholeDocumentation: boolean;
}

export interface DocumentationFileWithContentDTO {
  filename: string;
  extension: string;
  base64content: string;
}

export interface DocumentationFileWithContent {
  filename: string;
  extension: string;
  base64content: string;
}

export interface DocumentRequestStatusChangedDTO {
  id: number;
  playerId: number;
  zendeskId: number;
  newStatus: DocumentsRequestStatus;
  previousStatus: DocumentsRequestStatus;
  categories: DocumentsRequestCategory[];
  requestor: string;
  dateUpdated: Date;
}

export interface EmailTemplatesLabelsDTO {
  labels: EmailTemplateLabelDTO[];
}

export interface EmailTemplateLabelDTO {
  id: string;
  label: string;
}

export interface EmailTemplateLabel {
  id: string;
  label: string;
}

export interface EmailTemplate {
  id: string;
  content: string;
  subject: string;
}

export interface EmailTemplateDTO {
  id: string;
  content: string;
  subject: string;
}

export interface RenameFileDTO {
  newName: string;
}

export interface DocumentationCategoryOptionDTO {
  code: string;
  name: string;
  documentations: DocumentationOptionDTO[];
}

export interface DocumentationOptionDTO {
  code: string;
  name: string;
}

export interface RecategoriseFile {
  playerId: number;
  docRequestId: number;
  fileId: number;
  category: string;
  documentation: string;
  cardVerificationRequestId?: number;
}

export interface DocumentRequestFilter {
  playerId?: number;
  requestor?: string;
  categories?: DocumentsRequestCategory[];
  status?: DocumentsRequestStatus;
  zendeskId?: number;
  lockedBy?: string;
}

export interface DocumentLockDTO {
  agentEmail: string;
  agentFullName: string;
  dateCreated: Date;
}

export interface DocumentLock {
  agentEmail: string;
  agentFullName: string;
  dateCreated: Date;
}

export interface DocumentRequestsCountDTO {
  count: number;
}
